import type { ComponentPropsOptions, ExtractPublicPropTypes } from "vue";
import type { VerifyMfaBody } from "@/api/auth.api";

import { PinInputInput, PinInputRoot } from "reka-ui";
import { match } from "ts-pattern";
import { computed, defineComponent, onUnmounted, ref, watchEffect } from "vue";
import rest from "@/api/rest";
import Dialog from "@/components/primitives/dialog/Dialog";
import { openModal } from "@/components/providers/DialogProvider";
import Button from "@/components/ui/button/Button";
import Field from "@/components/ui/field/Field";
import Form from "@/components/ui/form/Form";
import Input from "@/components/ui/input/Input";
import { $t } from "@/services/i18n";
import { notifyError } from "@/utils/errors";
import { p } from "@/utils/props-builder";
import { toaster } from "./providers/ToasterProvider";

const props = {
  onSubmit: p.$func<(codes: VerifyMfaBody) => any>(),
  btnLabel: p.$string(),
  email: p.$string(),
  language: p.$string<"pt" | "en">(),
  full_name: p.$string(),
  onClose: p.func<() => any>(),
} satisfies ComponentPropsOptions;

type DisableMfaProps = ExtractPublicPropTypes<typeof props>;

const VerifyMfa = defineComponent({
  name: "VerifyMfa",
  props,
  setup(props) {
    const mode = ref<"auth" | "recovery" | "email">("auth");

    const pin = ref<string[]>([]);
    const pinArray = Array.from({ length: 6 }, (_, i) => i);
    const recoveryCode = ref("");
    const emailCode = ref<string[]>([]);

    const code = computed(() => {
      return match(mode.value)
        .with("auth", () => pin.value.join(""))
        .with("recovery", () => recoveryCode.value)
        .with("email", () => emailCode.value.join(""))
        .exhaustive();
    });

    const submit = () =>
      props.onSubmit({
        type: mode.value,
        code: code.value,
      });

    async function sendEmail() {
      await rest.mfa
        .sendEmail({
          email: props.email,
          language: props.language,
          full_name: props.full_name,
        })
        .catch(notifyError);

      toaster.info($t("mfa.email_sent"));
    }

    const email_sent = ref(false);

    watchEffect(async () => {
      if (mode.value === "email" && !email_sent.value) {
        await sendEmail();
        email_sent.value = true;
      }
    });

    const clearOtherFields = (currentMode: "auth" | "recovery" | "email") => {
      if (currentMode !== "auth") pin.value = [];
      if (currentMode !== "recovery") recoveryCode.value = "";
      if (currentMode !== "email") emailCode.value = [];
    };

    onUnmounted(() => props.onClose?.());

    const useRecoveryButton = () => (
      <Button
        onClick={() => {
          clearOtherFields("recovery");
          mode.value = "recovery";
        }}
        block
        variant="secondary"
        icon={"lucide:lock"}
      >
        {$t("mfa.use_recovery_code")}
      </Button>
    );

    const useAuthButton = () => (
      <Button
        onClick={() => {
          clearOtherFields("auth");
          mode.value = "auth";
        }}
        block
        variant="secondary"
        icon={"lucide:key"}
      >
        {$t("mfa.use_auth_app")}
      </Button>
    );

    const emailButtonText = computed(() => {
      return email_sent.value ? "mfa.enter_email_code" : "mfa.use_email_code";
    });

    const useEmailButton = () => (
      <Button
        onClick={() => {
          clearOtherFields("email");
          mode.value = "email";
        }}
        block
        variant="secondary"
        icon={"lucide:mail"}
      >
        {$t(emailButtonText.value)}
      </Button>
    );

    return () =>
      match(mode.value)
        .with("auth", () => (
          <>
            <Dialog.Header>
              <Dialog.Title>{$t("mfa.verify_mfa_title")}</Dialog.Title>
              <Dialog.Description>{$t("mfa.verify_mfa_auth_description")}</Dialog.Description>
            </Dialog.Header>
            <Form>
              <Dialog.Body class="pb-2">
                <PinInputRoot otp type="number" v-model={pin.value} class="center flex gap-2">
                  {pinArray.map((_, index) => (
                    <PinInputInput
                      v-focus={index === 0}
                      index={index}
                      class="h-12 w-12 rounded border border-gray-4 bg-transparent text-center text-foreground placeholder:text-muted-foreground/30 focus:border-brand-9"
                    />
                  ))}
                </PinInputRoot>
              </Dialog.Body>
              <Dialog.Footer>
                <div class="flex w-full flex-col">
                  <Button block onClick={submit} disabled={pin.value.length !== 6}>
                    {props.btnLabel}
                  </Button>

                  <div class="relative my-3 py-2">
                    <span class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-3 text-xs text-muted-foreground">
                      {$t("or")}
                    </span>
                    <hr />
                  </div>

                  <div class="space-y-3">
                    {useRecoveryButton()}
                    {useEmailButton()}
                  </div>
                </div>
              </Dialog.Footer>
            </Form>
          </>
        ))
        .with("recovery", () => (
          <>
            <Dialog.Header>
              <Dialog.Title>{$t("mfa.verify_mfa_title")}</Dialog.Title>
              <Dialog.Description>{$t("mfa.verify_mfa_recovery_description")}</Dialog.Description>
            </Dialog.Header>
            <Dialog.Body>
              <Form>
                <Field label={$t("recovery_code")} hideFooter>
                  <Input v-model={recoveryCode.value} v-focus />
                </Field>
              </Form>
            </Dialog.Body>
            <Dialog.Footer>
              <div class="flex w-full flex-col">
                <Button block onClick={submit}>
                  {$t(props.btnLabel as any)}
                </Button>

                <div class="relative my-3 py-2">
                  <span class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-3 text-xs text-muted-foreground">
                    {$t("or")}
                  </span>
                  <hr />
                </div>
                <div class="space-y-3">
                  {useAuthButton()}
                  {useEmailButton()}
                </div>
              </div>
            </Dialog.Footer>
          </>
        ))
        .with("email", () => (
          <>
            <Dialog.Header>
              <Dialog.Title>{$t("mfa.verify_mfa_title")}</Dialog.Title>
              <Dialog.Description>{$t("mfa.verify_mfa_email_description")}</Dialog.Description>
            </Dialog.Header>
            <Form>
              <Dialog.Body class="pb-2">
                <Form>
                  <Field label={$t("code")} hideFooter>
                    <PinInputRoot
                      otp
                      type="number"
                      v-model={emailCode.value}
                      class="center flex gap-2"
                    >
                      {pinArray.map((_, index) => (
                        <PinInputInput
                          v-focus={index === 0}
                          index={index}
                          class="h-12 w-12 rounded border border-gray-4 bg-transparent text-center text-foreground placeholder:text-muted-foreground/30 focus:border-brand-9"
                        />
                      ))}
                    </PinInputRoot>
                  </Field>
                  <Button onClick={sendEmail} class="mt-4" block variant="subtle">
                    {$t("mfa.resend_email")}
                  </Button>
                </Form>
              </Dialog.Body>
              <Dialog.Footer>
                <div class="flex w-full flex-col">
                  <Button block onClick={submit}>
                    {props.btnLabel}
                  </Button>

                  <div class="relative my-3 py-2">
                    <span class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-3 text-xs text-muted-foreground">
                      {$t("or")}
                    </span>
                    <hr />
                  </div>
                  <div class="space-y-3">
                    {useRecoveryButton()}
                    {useAuthButton()}
                  </div>
                </div>
              </Dialog.Footer>
            </Form>
          </>
        ))
        .exhaustive();
  },
});

export function openVerifyMfa(props: DisableMfaProps) {
  openModal(<VerifyMfa {...props} />, { size: "sm" });
}
